<template>
  <article
    class="teaser-product teaser-product--variation _js-product-variation"
    :data-variation-id="product.key"
    :data-variation="trackingAsString"
  >
    <a
      :href="product.link"
      class="teaser-product__link-image"
      tabindex="0"
      :aria-label="`${product.brand} ${product.title}`"
      @click="clickLink"
      @click.middle="middleClickLink"
      @click.right="rightClickLink"
    >
      <h3 class="sr-only">{{ product.brand }} {{ product.title }}</h3>
      <div
        v-if="product.isNews || product.isInStockOnWeb"
        class="teaser-product__not-available"
      >
        {{ $t("soldOut") }}
      </div>
      <div class="teaser-product__photos">
        <div class="teaser-product__photo">
          <ResponsiveImage
            v-if="productImage"
            width="642"
            height="856"
            img-class="teaser-product__image"
            :src="fallbackImage"
            :sources="imageSources"
          />
        </div>
        <div
          v-if="hoverColor && hoverColor.fallbackImage"
          class="teaser-product__photo teaser-product__photo--variant"
        >
          <ResponsiveImage
            width="642"
            height="856"
            img-class="teaser-product__image"
            :src="hoverColor.fallbackImage"
            :sources="hoverColor.imageSources"
            :lazy="false"
          />
        </div>
        <div v-if="!props.hidePromotion" class="label__container">
          <template v-if="memberPromotion">
            <span :class="`label ${memberPromotion?.className}`">
              {{ memberPromotion.text }}
            </span>
          </template>
          <template v-if="promotion">
            <span :class="`label ${promotion.className}`">
              {{ promotion.text }}
            </span>
          </template>
        </div>
      </div>
    </a>
    <div class="teaser-product__elements">
    <div class="teaser-product__content">
      <!-- Favorite button -->
      <div
        v-if="!props.hideFavorites && epiProductCode"
        class="teaser-product__lovebutton"
      >
        <button
          type="button"
          class="btn btn--small btn--transparent love"
          :class="{
            'is-loved': isFavorite,
          }"
          :disabled="favoriteStore.isLoadingFavorites"
          :title="$t(title)"
          :aria-expanded="!isUserAuthenticated ? isLoginActive : undefined"
          :aria-controls="!isUserAuthenticated ? 'login' : undefined"
          @click="clickFavorite()"
        >
          <Icon :type="IconTypes.Heart" :size="IconSizes.Tiny" />
        </button>
      </div>
      <!-- Colors -->
      <div class="teaser-product__colors" v-if="!props.hideColors">
        <a
          v-for="color in colorVariationsToDisplay"
          :key="color.rgb"
          :href="color.url"
          :class="[
            `item-color`,
            color.isSelf ? `self` : null,
            color.meta ? `item-color--${color.meta}` : null,
          ]"
          :style="color.rgb ? `--inline-background-color: rgb(${color.rgb}); background-color: rgb(${color.rgb});` : null"
          :aria-label="`${color.name} (${color.meta}) ${product.title}`"
          @mouseover="mouseOverColor(color)"
          @mouseout="mouseOutColor()"
        ></a>
        <span v-if="remainingColorVariations > 0" class="remaining-colors">
          +{{ remainingColorVariations }}
        </span>
      </div>
      <!-- Brand -->
      <div class="teaser-product__brand">
        {{ product.brand }}
      </div>
      <!-- Sizes -->
      <template v-if="!props.hideSizes && isInStock">
        <div v-if="sizes" class="teaser-product__sizes">
          <template v-if="isMatrixSize">
            <span>{{ $t("availableInManySizes") }}</span>
          </template>
          <template v-else>
            <span
              v-for="size in sizes"
              :key="size.label"
              :class="{
                'teaser-product__sizes__outofstock': !size.available,
              }"
              >{{ formatSize(size.label) }}
            </span>
          </template>
        </div>
      </template>
      <template v-if="!props.hideSizes && !isInStock">
        <div class="teaser-product__sizes">
          <span>{{ $t("onlyInStores") }}</span>
        </div>
      </template>
      <!-- Highlight text (not sure if used) -->
      <div
        v-if="product.custom.HighlightText"
        class="teaser-product__highlight-text"
      >
        {{ product.custom.HighlightText[0].label }}
      </div>
      <!-- Name -->
      <div class="teaser-product__name">
        <a
          :href="product.link"
          @click="clickLink"
          @click.middle="middleClickLink"
          @click.right="rightClickLink"
        >
          {{ product.title }}
        </a>
      </div>
      <!-- Price -->
      <div class="teaser-product__price">
        <div v-if="price" class="price">
          {{ price }}
        </div>
        <div v-if="newPrice" class="price price--new">
          {{ newPrice }}
        </div>
        <div v-if="oldPrice" class="price price--old">
          {{ oldPrice }}
        </div>
      </div>
      <div v-if="product.custom.MemberPromotion || product.custom.Promotion" class="teaser-product__promotion-texts">
        <!-- Member promotion -->
        <div
          v-if="product.custom.MemberPromotion"
          class="teaser-product__members-promotions-text"
        >
          {{ product.custom.MemberPromotion[0].label }}
        </div>
        <!-- Normal promotion -->
        <div
          v-if="product.custom.Promotion"
          class="teaser-product__promotions-text"
        >
          {{ product.custom.Promotion[0].label }}
        </div>
      </div>
    </div>

      <!-- Quick add button -->
      <button
        v-if="isInStock && isQuickAdd"
        class="btn btn--small btn--transparent teaser-product__quickadd"
        type="button"
        @click="openQuickAddDialog"
      >
        {{ $t("quickAdd") }}
      </button>

      <!-- PopupDialog with AddToCartForm slotted -->
        <dialog
          v-if="isInStock && isQuickAdd"
          ref="quickAddDialogElement"
          class="quick-add-dialog"
          :class="{ 'is-visible': isDialogVisible }"
          aria-live="polite"
          :aria-hidden="!isDialogVisible"
          role="dialog"
          aria-labelledby="quick-add-dialog-heading"
          @click.self="closeQuickAddDialog"
        >
          <article class="quick-add-dialog__content">
            <!-- Close Button -->
            <button
              class="btn btn--small btn--link btn--close quick-add-dialog__close"
              type="button"
              @click.prevent="closeQuickAddDialog"
              :title="$t('close')"
            >
              <Icon type="close" />
            </button>

            <div class="quick-add-dialog__body">
              <div class="quick-add-dialog__image">
                <a
                  :href="product.link"
                  class="quick-add-dialog__image--link"
                  tabindex="0"
                  :aria-label="`${product.brand} ${product.title}`"
                  @click="clickLink"
                  @click.middle="middleClickLink"
                  @click.right="rightClickLink"
                >
                  <picture>
                    <source type="image/webp" :srcset="fallbackImage" />
                    <img
                      alt=""
                      width="auto"
                      height="auto"
                      :src="fallbackImage"
                      :sources="imageSources"
                    />
                  </picture>
                  <div v-if="!props.hidePromotion" class="label__container">
                    <template v-if="memberPromotion">
                      <span :class="`label ${memberPromotion?.className}`">
                        {{ memberPromotion.text }}
                      </span>
                    </template>
                    <template v-if="promotion">
                      <span :class="`label ${promotion.className}`">
                        {{ promotion.text }}
                      </span>
                    </template>
                  </div>
                </a>
                <div class="quick-add-dialog__product-info quick-add-dialog__product-info--mobile">
                  <div class="product__info">
                    <h3 class="product__brand">{{ product.brand }}</h3>
                    <span class="product__name product__name--large">
                      <a
                        :href="product.link"
                        @click="clickLink"
                        @click.middle="middleClickLink"
                        @click.right="rightClickLink"
                      >
                        {{ product.title }}
                      </a>
                    </span>
                    <p class="product__price product__price--large">
                      <div v-if="price" class="price">
                        {{ price }}
                      </div>
                      <div v-if="newPrice" class="price price--new">
                        {{ newPrice }}
                      </div>
                      <div v-if="oldPrice" class="price price--old">
                        {{ oldPrice }}
                      </div>
                    </p>
                  </div>
                </div>
              </div>
              <div class="quick-add-dialog__main">
                <!-- Header with Product Info -->
                <div class="quick-add-dialog__product-info quick-add-dialog__product-info--desktop">
                  <div class="product__info">
                    <h3 class="product__brand">{{ product.brand }}</h3>
                    <span class="product__name product__name--large">
                      <a
                        :href="product.link"
                        @click="clickLink"
                        @click.middle="middleClickLink"
                        @click.right="rightClickLink"
                      >
                        {{ product.title }}
                      </a>
                    </span>
                    <!-- Price -->
                    <p class="product__price product__price--large">
                      <div v-if="price" class="price">
                        {{ price }}
                      </div>
                      <div v-if="newPrice" class="price price--new">
                        {{ newPrice }}
                      </div>
                      <div v-if="oldPrice" class="price price--old">
                        {{ oldPrice }}
                      </div>
                    </p>
                    <div v-if="product.custom.MemberPromotion || product.custom.Promotion" class="teaser-product__promotion-texts">
                      <!-- Member promotion -->
                      <div
                        v-if="product.custom.MemberPromotion"
                        class="teaser-product__members-promotions-text"
                      >
                        {{ product.custom.MemberPromotion[0].label }}
                      </div>
                      <!-- Normal promotion -->
                      <div
                        v-if="product.custom.Promotion"
                        class="teaser-product__promotions-text"
                      >
                        {{ product.custom.Promotion[0].label }}
                      </div>
                    </div>
                  </div>
                </div>
                <!-- AddToCartForm -->
                <div class="quick-add-dialog__form">
                  <AddToCartForm v-if="isDialogVisible" v-bind="addToCartFormProps" :key="dialogKey" />
                </div>
              </div>
            </div>
          </article>
        </dialog>
    </div>
  </article>
</template>

<script setup lang="ts">
import { notifyClick } from "../../mixins/notifyClick";
import { getProductClickEvent, pushDataToGtm } from "../../mixins/tracking";
import {
  ProductVariant,
  Product,
  ProductImage,
  ProductImageSource,
  ProductGroup,
} from "../../services/search.service";
import Icon from "../../components/icon/icon.vue";
import { IconSizes, IconTypes } from "../../components/icon/icon.model";
import ResponsiveImage from "../../components/responsiveImage/responsiveImage.vue";
import { formatSize} from "../../mixins/productSize.mixin";
import {
  computeMemberPromotion,
  computePromotion,
} from "../../mixins/productBadge.mixin";
import {
  computeNewPrice,
  computeOldPrice,
  computePrice,
  computeRebate,
  priceAsNumber,
} from "../../mixins/productPrice.mixin";
import { getCustomApptusValue } from "../../utils/search.utils";
import { computed, ref, onUnmounted, watch, onMounted } from "vue";
import { useFavoritesStore } from "../../stores/favoritesStore";
import GtmService from "../../services/gtm.service";
import AddToCartForm, {
  AddToCartFormProps,
} from "../addToCartForm/addToCartForm.vue";
import { mapProductsToAddToCartFormProps } from "../../mixins/addToCart.mixin";

const props = defineProps<{
  userId: string;
  productGroup: ProductGroup;
  product: Product;
  isUserAuthenticated: boolean;
  useGtmTracking: boolean;
  isLoadingProducts?: boolean;
  isLoadingFavorites?: boolean;
  smallLabel?: boolean;
  hideSizes?: boolean;
  hideColors?: boolean;
  hidePromotion?: boolean;
  hideFavorites?: boolean;
  trackSelectItem?: boolean;
  itemListId?: string;
  itemListName?: string;
  itemListIndex?: number;
  upsellAlgorithm?: string;
  store?: string;
  isQuickAdd?: boolean;
  teaserProductSize?: string;
}>();

const isLoginActive = ref(false);
const hoverColor = ref<HoverColor>();
const favoriteStore = useFavoritesStore();
const quickAddDialogElement = ref<HTMLDialogElement | null>(null);
const isDialogVisible = ref(false);
const dialogKey = ref(0); // Unique key to force re-render of AddToCartForm
const breakpoint = 1025;
let mediaQueryMax: MediaQueryList | null = null;
let mediaQueryMin: MediaQueryList | null = null;

const openQuickAddDialog = () => {
  if (quickAddDialogElement.value) {
    isDialogVisible.value = true;
    dialogKey.value++; // Change key to force unmount/mount
    quickAddDialogElement.value.showModal();
  } else {
    console.warn("quickAddDialogElement is not ready yet.");
  }
};

const closeQuickAddDialog = () => {
  isDialogVisible.value = false;
  quickAddDialogElement.value?.close();
};

const handleBreakpointChange = () => {
  if (isDialogVisible.value) {
    closeQuickAddDialog();
  }
};

const addToCartFormProps = computed<AddToCartFormProps>(() => {
  return mapProductsToAddToCartFormProps(
    props.product.variants,
    isMatrixSize.value,
    props.product,
    props.store ?? "",
    closeQuickAddDialog,
    props.itemListId,
    props.itemListName
  );
});

const isFavorite = computed(() => {
  if (!favoriteStore) {
    return false;
  }
  if (!epiProductCode.value) {
    return false;
  }

  return favoriteStore.isFavorite(epiProductCode.value);
});

const newPrice = computed(() => {
  return computeNewPrice(props.product);
});

const oldPrice = computed(() => {
  return computeOldPrice(props.product);
});

const price = computed(() => {
  return computePrice(props.product);
});

const rebate = computed(() => {
  return computeRebate(props.product);
});

const promotion = computed(() => {
  return computePromotion(props.product, props.smallLabel);
});

const memberPromotion = computed(() => {
  return computeMemberPromotion(props.product, props.smallLabel);
});

const title = computed(() => {
  return isFavorite.value ? "removeFavorite" : "addFavorite";
});

const epiProductCode = computed((): string => {
  return props.product.custom.EpiPoductCode[0].label;
});

type Color = {
  isSelf: boolean;
  rgb: string;
  name: string;
  url: string;
  meta: string;
  imageAlt: string;
  fallbackImage: string;
  imageSources: Array<{
    srcset: string;
    media: string;
    type: string;
  }>;
};
type HoverColor = Color | null;

const colors = computed((): Color[] => {
  const colorList: Color[] = [];
  const validProducts = props.productGroup.products;

  validProducts.forEach((product) => {
    const productImage = product.imageInfo?.images?.[0];

    const color: Color = {
      isSelf: product === props.product,
      rgb: product.custom?.RGBColorCode?.[0]?.label || "",
      name: product.custom?.ColorName?.[0]?.label || "",
      url: product.link,
      meta: product.custom?.MetaColor?.[0]?.label?.toLowerCase() || "",
      imageAlt: productImage?.alt || "",
      fallbackImage: productImage ? getFallbackImage(productImage) : "",
      imageSources: productImage ? getImageSources(productImage) : [],
    };

    colorList.push(color);
  });

  return colorList;
});

const colorsDisplayCount = computed(() =>
  Math.min(props.store === "JeanPaul" ? 5 : 7, colors.value.length),
);

const colorVariationsToDisplay = computed(() =>
  colors.value.slice(0, colorsDisplayCount.value),
);

const remainingColorVariations = computed(
  () => colors.value.length - colorsDisplayCount.value,
);

const isMatrixSize = computed((): boolean => {
  return props.product.custom?.IsMatrixSize?.[0]?.label === "True";
});

const metaColor = computed((): string => {
  const product = props.product as Product;

  return product.custom?.MetaColor?.[0]?.label;
});

const department = computed((): string => {
  const product = props.product as Product;

  return product.custom?.Dept?.[0]?.label;
});

const category = computed((): string => {
  const product = props.product as Product;

  return getCustomApptusValue(product, "Category");
});

const gtmStockStatus = computed((): string => {
  const product = props.product as Product;

  return product.custom?.GtmStockStatus?.[0]?.label;
});

const isInStock = computed((): boolean => {
  return (props.product as Product).inStock;
});

const variantsInStock = computed((): ProductVariant[] => {
  const product = props.product as Product;

  return (product.variants || []).filter((variant: ProductVariant) => {
    return !!variant.inStock;
  });
});

const sizes = computed(
  (): Array<{
    label: string;
    available: boolean;
  }> => {
    return (variantsInStock.value || [])
      .filter((variant: ProductVariant) => {
        return !!variant.size;
      })
      .map((variant: ProductVariant) => {
        return {
          label: variant.size as string,
          available: true,
        };
      });
  },
);

const productImage = computed((): ProductImage | undefined => {
  if (props.product.imageInfo && props.product.imageInfo.images) {
    return props.product.imageInfo.images[0];
  }
  return undefined;
});

const fallbackImage = computed((): string => {
  if (!productImage.value) {
    return "";
  } else {
    return getFallbackImage(productImage.value);
  }
});

const imageSources = computed(
  ():
    | Array<{
        srcset: string;
        media: string;
        type: string;
      }>
    | undefined => {
    if (!productImage.value) {
      return;
    }

    return getImageSources(productImage.value);
  },
);

const tracking = computed((): object | undefined => {
  if (!props.useGtmTracking) {
    return undefined;
  }

  const tracking = getProductClickEvent({
    userId: props.userId || "",
    department: department.value,
    brand: props.product.brand,
    category: category.value,
    rebate: rebate.value,
    stock: gtmStockStatus.value,
    price: priceAsNumber(props.product),
    metaColor: metaColor.value,
    key: props.product.key,
    title: props.product.title,
  });

  return tracking;
});

const trackingAsString = computed((): string | undefined => {
  if (!props.useGtmTracking) {
    return undefined;
  }

  return JSON.stringify(tracking.value);
});

onMounted(() => {
  // Detect going below 1025px
  mediaQueryMax = window.matchMedia(`(max-width: ${breakpoint}px)`);
  mediaQueryMax.addEventListener("change", handleBreakpointChange);

  // Detect going above 1025px
  mediaQueryMin = window.matchMedia(`(min-width: ${breakpoint + 1}px)`);
  mediaQueryMin.addEventListener("change", handleBreakpointChange);
});

onUnmounted(() => {
  if (mediaQueryMax) {
    mediaQueryMax.removeEventListener("change", handleBreakpointChange);
  }
  if (mediaQueryMin) {
    mediaQueryMin.removeEventListener("change", handleBreakpointChange);
  }
  const loginModalEl = document.getElementById("login");

  if (loginModalEl) {
    loginModalEl.removeEventListener("elementToggled", hideLogin);
  }
});

watch(() => isDialogVisible, (newVal) => {
  if (newVal) {
    quickAddDialogElement.value?.showModal();
  } else {
    quickAddDialogElement.value?.close();
  }
});

/**
 * Click link
 */
function clickLink($event: Event) {
  notifyClick($event, props.product);

  if (props.useGtmTracking) {
    pushDataToGtm(tracking);
  }

  if (props.trackSelectItem) {
    GtmService.pushSelectItemEvent(
      props.itemListId ?? "",
      props.itemListName ?? "",
      props.isUserAuthenticated,
      props.product,
      undefined,
      props.itemListIndex,
      props.upsellAlgorithm,
    );
  }
}

/**
 * Middle click link
 */
function middleClickLink($event: Event) {
  notifyClick($event, props.product);
}

/**
 * Right click link
 */
function rightClickLink($event: Event) {
  notifyClick($event, props.product);
}

/**
 * Click favorite
 */
function clickFavorite(): void {
  if (props.isUserAuthenticated) {
    return toggleFavorite();
  }
  if (favoriteStore.isLoadingFavorites) {
    return;
  }
  displayLogin();
}

/**
 * Display login
 */
function displayLogin() {
  const loginModalEl = document.getElementById("login");

  if (loginModalEl) {
    loginModalEl.classList.toggle("is-active");

    isLoginActive.value = true;

    loginModalEl.addEventListener("elementToggled", hideLogin);
  }
}

/**
 * Hide login
 */
function hideLogin() {
  const loginModalEl = document.getElementById("login");

  isLoginActive.value = false;

  if (loginModalEl) {
    loginModalEl.removeEventListener("elementToggled", hideLogin);
  }
}

/**
 * Toggle favorite
 */
function toggleFavorite(): void {
  if (!epiProductCode.value) {
    return;
  }

  favoriteStore.toggleFavorite(epiProductCode.value);
}

/**
 * Mouse over color
 */
function mouseOverColor(color) {
  hoverColor.value = color;
}

/**
 * Mouse out color
 */
function mouseOutColor() {
  hoverColor.value = null;
}

function getFallbackImage(productImage: ProductImage): string {
  return productImage.sources[productImage.sources.length - 1].url;
}

function getImageSources(productImage: ProductImage): Array<{
  srcset: string;
  media: string;
  type: string;
}> {
  const sources = productImage.sources.slice(0, -1);

  return sources.map((source: ProductImageSource, i: number) => {
    const isLast = i === sources.length - 1;

    return {
      srcset: source.url,
      media: isLast ? "" : `(max-width: ${source.width * 2}px)`,
      type: "image/webp",
    };
  });
}
</script>
